import {
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { CashRegisterIcon } from "../../components/CashRegisterIcon";
import { formatarCnpj } from "../../utils/FormatarDocumento";
import {
  Menu,
  X,
  AlertTriangle,
  DollarSign,
  BarChart3,
  CreditCard,
  Stethoscope,
  Users,
  User,
  Plus,
  FileText,
  FilePlus,
  LogOut,
  ChevronDown,
  ChevronRight,
  Edit2,
  Save,
  Receipt,
  Eye,
  EyeOff,
  Inbox,
  PackageMinus,
  HandHeart,
  ArrowUpDown,
  Layers,
  Layers2,
  Landmark,
  Building2,
  Settings,
  Zap,
  Truck,
  Clock,
  ClipboardList,
} from "lucide-react";
import { usuarioService } from "../../services/apiService";

const getNomeOrganizacaoExibicao = (organizacao) =>
  organizacao?.nomeFantasia || organizacao?.nome || "Organização";

const BarraNavegacao = ({
  usuario,
  onLogout,
  isOpen,
  onToggle,
  possuiPermissao,
  onUserUpdate,
  sessaoOrganizacao,
  onTrocarOrganizacao,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    id: "",
    nome: "",
    email: "",
    password: "",
    confirmPassword: "",
    permissao: "",
    ativo: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1050);
  const [activeBottomMenu, setActiveBottomMenu] = useState(null);
  const [showOrganizationSelector, setShowOrganizationSelector] =
    useState(false);
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();
  const navRef = useRef(null);
  const menuButtonRefs = useRef({});
  const lastNavScrollTopRef = useRef(0);
  const lastFocusedMenuKeyRef = useRef(null);

  const [expandedMenus, setExpandedMenus] = useState({
    caixa: false,
    ponto: false,
    "fundos-reserva": false,
    cadastros: false,
    "contas-pagar": false,
    relatorios: false,
    configuracao: false,
  });
  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1050);
      if (window.innerWidth >= 1050) {
        setActiveBottomMenu(null);
        setShowOrganizationSelector(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadUserProfile = useCallback(async () => {
    try {
      if (usuario.id) {
        const userData = await usuarioService.listarUsuarioPorId(usuario.id);
        setProfileData({
          id: userData.id,
          nome: userData.nome,
          email: userData.email,
          password: "",
          confirmPassword: "",
          permissao: userData.permissao,
          ativo: userData.ativo,
        });
      } else {
        setProfileData({
          id: "",
          nome: usuario.nome || "",
          email: usuario.email || "",
          password: "",
          confirmPassword: "",
          permissao: usuario.permissao || "",
          ativo: usuario.ativo !== undefined ? usuario.ativo : true,
        });
      }
    } catch (err) {}
  }, [usuario]);

  useEffect(() => {
    if (usuario && showEditProfile) {
      loadUserProfile();
    }
  }, [loadUserProfile, showEditProfile, usuario]);

  useLayoutEffect(() => {
    if (isMobile) {
      return;
    }

    const navElement = navRef.current;
    if (!navElement) {
      return;
    }

    navElement.scrollTop = lastNavScrollTopRef.current;

    if (!lastFocusedMenuKeyRef.current) {
      return;
    }

    const focusedButton = menuButtonRefs.current[lastFocusedMenuKeyRef.current];
    if (focusedButton && document.activeElement !== focusedButton) {
      focusedButton.focus({ preventScroll: true });
    }
  }, [expandedMenus, isMobile, isOpen, location.pathname]);

  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSaveProfile = async () => {
    if (!profileData.nome.trim()) {
      showError("Nome é obrigatório");
      return;
    }

    if (!profileData.email.trim()) {
      showError("Email é obrigatório");
      return;
    }

    if (!validateEmail(profileData.email)) {
      showError("Email deve ter um formato válido");
      return;
    }

    if (
      profileData.password &&
      profileData.password !== profileData.confirmPassword
    ) {
      showError("Senhas não coincidem");
      return;
    }

    if (profileData.password && profileData.password.length < 6) {
      showError("Senha deve ter pelo menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        nome: profileData.nome,
        email: profileData.email,
        permissao: profileData.permissao,
        ativo: profileData.ativo,
      };

      if (profileData.password.trim()) {
        dataToSend.password = profileData.password;
      }

      if (profileData.id) {
        await usuarioService.atualizarUsuario(profileData.id, dataToSend);
      }

      showSuccess("Perfil atualizado com sucesso!");

      const updatedUser = {
        ...usuario,
        nome: profileData.nome,
        email: profileData.email,
      };

      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }

      setTimeout(() => {
        setShowEditProfile(false);
      }, 2000);
    } catch (err) {
      showError(err.message || "Erro ao atualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = (menuKey) => {
    if (navRef.current) {
      lastNavScrollTopRef.current = navRef.current.scrollTop;
    }
    lastFocusedMenuKeyRef.current = menuKey;
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const handleLogout = async () => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja sair do sistema?",
    });

    if (!confirmed) {
      return;
    }

    onLogout();
    navigate("/login");
  };

  const handleTrocaOrganizacao = async (event) => {
    const novaOrganizacaoId = event.target.value;
    if (
      !novaOrganizacaoId ||
      novaOrganizacaoId === sessaoOrganizacao?.organizacaoId
    ) {
      return;
    }
    try {
      await onTrocarOrganizacao?.(novaOrganizacaoId);
      showSuccess("Organização alterada com sucesso.");
      setShowOrganizationSelector(false);
    } catch (err) {
      showError(err.message || "Erro ao trocar organização.");
    }
  };

  const navigateTo = (path, menuKey = null) => {
    if (navRef.current) {
      lastNavScrollTopRef.current = navRef.current.scrollTop;
    }
    lastFocusedMenuKeyRef.current = menuKey;
    navigate(path);
    setActiveBottomMenu(null); // Fechar menu bottom ao navegar
    setShowOrganizationSelector(false);
  };

  const isAdmin = possuiPermissao("admin");
  const isSuperAdmin = possuiPermissao("super_admin");

  const menuItems = [
    {
      key: "caixa",
      label: "Caixa",
      icon: Layers2,
      hasSubmenu: true,
      roles: ["USER", "ADMIN"],
      submenu: [
        {
          key: "caixa-diario",
          label: "Caixa Diário",
          path: "/",
          icon: DollarSign,
          roles: ["USER", "ADMIN"],
        },
        {
          key: "caixa-geral",
          label: "Caixa Geral",
          path: "/caixa-geral",
          icon: Layers,
          roles: ["ADMIN"],
        },
      ],
    },
    {
      key: "ponto",
      label: "Ponto",
      icon: Clock,
      hasSubmenu: true,
      roles: ["USER", "ADMIN"],
      submenu: [
        {
          key: "meu-ponto",
          label: "Bater ponto",
          path: "/meu-ponto",
          icon: Clock,
          roles: ["USER", "ADMIN"],
        },
        {
          key: "gestao-ponto",
          label: "Gestão do ponto",
          path: "/gestao-ponto",
          icon: ClipboardList,
          roles: ["ADMIN"],
        },
      ],
    },
    {
      key: "reabertura-caixa",
      label: "Reabertura de Caixa",
      icon: HandHeart,
      hasSubmenu: true,
      roles: ["USER"],
      submenu: [
        {
          key: "solicitacao-reabertura",
          label: "Minhas Solicitações",
          path: "/solicitacao-caixa",
          icon: ArrowUpDown,
          roles: ["USER"],
        },
      ],
    },
    {
      key: "fundos-reserva",
      label: "Fundos de Reserva",
      icon: DollarSign,
      hasSubmenu: true,
      roles: ["ADMIN"],
      submenu: [
        {
          key: "fundo-reserva-adicionar",
          label: "Adicionar",
          path: "/fundo-reserva",
          icon: Plus,
          roles: ["ADMIN"],
        },
        {
          key: "gerenciamento-socios",
          label: "Sócios",
          path: "/socios",
          icon: Users,
          roles: ["ADMIN"],
        },
        {
          key: "gerenciamento-contas-bancarias",
          label: "Contas Bancárias",
          path: "/conta-bancaria",
          icon: Landmark,
          roles: ["ADMIN"],
        },
        {
          key: "fundo-reserva-movimentacao",
          label: "Movimentação",
          path: "/movimento-fundo-reserva",
          icon: ArrowUpDown,
          roles: ["ADMIN"],
        },
      ],
    },
    {
      key: "estoque",
      label: "Estoque",
      icon: Receipt,
      hasSubmenu: true,
      roles: ["ADMIN", "USER"],
      submenu: [
        {
          key: "lista-compras",
          label: "Lista de Compras",
          path: "/lista-compras",
          icon: Receipt,
          roles: ["ADMIN", "USER"],
        },
        {
          key: "avaliar-estoque",
          label: "Avaliar estoque",
          path: "/avaliar-estoque",
          icon: BarChart3,
          roles: ["ADMIN", "USER"],
        },
        {
          key: "entrada-produtos",
          label: "Entrada de produtos",
          path: "/entrada-produtos",
          icon: Inbox,
          roles: ["ADMIN", "USER"],
        },
        {
          key: "saidas-estoque",
          label: "Saídas de estoque",
          path: "/saida-estoque",
          icon: PackageMinus,
          roles: ["ADMIN", "USER"],
        },
        {
          key: "perdas-avarias",
          label: "Perdas e avarias",
          path: "/perdas-avarias",
          icon: PackageMinus,
          roles: ["ADMIN", "USER"],
        },
        {
          key: "relatorios",
          label: "Relatórios",
          path: "/relatorios",
          icon: BarChart3,
          roles: ["ADMIN", "USER"],
        },
      ],
    },
    {
      key: "cadastros",
      label: "Cadastros",
      icon: FilePlus,
      hasSubmenu: true,
      roles: ["ADMIN"],
      submenu: [
        {
          key: "organizacoes",
          label: "Organizações",
          path: "/organizacoes",
          icon: Building2,
          roles: ["SUPER_ADMIN"],
        },
        {
          key: "usuarios",
          label: "Usuários",
          path: "/usuarios",
          icon: Users,
          roles: ["ADMIN"],
        },
        {
          key: "tipo-caixa",
          label: "Tipos de Caixa",
          path: "/tipo-caixa",
          icon: Layers2,
          roles: ["ADMIN"],
        },
        {
          key: "profissionais",
          label: "Profissionais",
          path: "/profissionais",
          icon: User,
          roles: ["ADMIN"],
        },
        {
          key: "especialidades",
          label: "Especialidades",
          path: "/especialidades",
          icon: Stethoscope,
          roles: ["ADMIN"],
        },
        {
          key: "formas-pagamento",
          label: "Formas de Pagamento",
          path: "/formas-pagamento",
          icon: CreditCard,
          roles: ["ADMIN"],
        },
        {
          key: "fornecedores",
          label: "Fornecedores",
          path: "/fornecedores",
          icon: Truck,
          roles: ["ADMIN"],
        },
        {
          key: "unidades-controle",
          label: "Unidades de Controle",
          path: "/unidades-controle",
          icon: Layers2,
          roles: ["ADMIN"],
        },
        {
          key: "produtos",
          label: "Produtos",
          path: "/produtos",
          icon: FilePlus,
          roles: ["ADMIN"],
        },
        {
          key: "cadastro de impedimentos",
          label: "Cadastro de impedimentos",
          path: "/impedimento",
          icon: AlertTriangle,
          roles: ["ADMIN"],
        },
      ],
    },
    {
      key: "gerenciamento",
      label: "Gerenciamento",
      icon: Inbox,
      hasSubmenu: true,
      roles: ["ADMIN"],
      submenu: [
        {
          key: "solicitacao-caixa-admin",
          label: "Solicitações de Caixa",
          path: "/solicitacao-caixa-admin",
          icon: ArrowUpDown,
          roles: ["ADMIN"],
        },
        {
          key: "folha-pagamento",
          label: "Folha de Pagamento",
          path: "/folha-pagamento",
          icon: Receipt,
          roles: ["ADMIN"],
        },
      ],
    },
    {
      key: "contas-pagar",
      label: "Contas a Pagar",
      icon: Receipt,
      hasSubmenu: true,
      roles: ["ADMIN"],
      submenu: [
        {
          key: "contas-pagar-despesas",
          label: "Despesas",
          path: "/despesas",
          icon: Receipt,
          roles: ["ADMIN"],
        },
        {
          key: "contas-pagar-grupos",
          label: "Grupos",
          path: "/grupos",
          icon: Layers,
          roles: ["ADMIN"],
        },
        {
          key: "contas-pagar-subgrupos",
          label: "Subgrupos",
          path: "/subgrupos",
          icon: Layers2,
          roles: ["ADMIN"],
        },
        {
          key: "contas-pagar-maquinas",
          label: "Máquinas de Cartão",
          path: "/maquina-cartao",
          icon: Receipt,
          roles: ["ADMIN"],
        },
      ],
    },
    {
      key: "relatorios",
      label: "Relatórios",
      icon: FileText,
      hasSubmenu: true,
      roles: ["ADMIN"],
      submenu: [
        {
          key: "Grafico",
          label: "Gráfico do Caixa",
          path: "/grafico-caixa",
          icon: BarChart3,
          roles: ["ADMIN"],
        },
        {
          key: "entrada-caixa",
          label: "Entrada de Caixa",
          path: "/entrada-caixa",
          icon: BarChart3,
          roles: ["ADMIN"],
        },
        {
          key: "apuracao-mensal-fundos",
          label: "Apuração Mensal Fundos",
          path: "/apuracao-mensal-fundos",
          icon: BarChart3,
          roles: ["ADMIN"],
        },
        {
          key: "dashboard-despesas",
          label: "Dashboard Despesas",
          path: "/dashboard-despesas",
          icon: BarChart3,
          roles: ["ADMIN"],
        },
        {
          key: "resumo-profissional",
          label: "Resumo Profissional",
          path: "/resumo-profissional",
          icon: BarChart3,
          roles: ["ADMIN"],
        },
        {
          key: "comparativo-financeiro",
          label: "Comparativo Financeiro",
          path: "/comparativo-financeiro",
          icon: BarChart3,
          roles: ["ADMIN"],
        },
      ],
    },
    {
      key: "configuracao",
      label: "Configuração",
      icon: Settings,
      hasSubmenu: true,
      roles: ["ADMIN"],
      submenu: [
        {
          key: "configuracao-caixa-sisodonto",
          label: "Integração Sisodonto",
          path: "/configuracao-caixa-sisodonto",
          icon: Zap,
          roles: ["ADMIN"],
        },
      ],
    },
  ];

  const estoqueMenu = menuItems.find((item) => item.key === "estoque");
  if (estoqueMenu) {
    const operacao = estoqueMenu.submenu.filter((item) => !item.group);
    estoqueMenu.submenu = [
      { key: "estoque-operacao", group: "Operação", roles: ["ADMIN", "USER"] },
      ...operacao,
      {
        key: "estoque-cadastros",
        group: "Cadastros",
        roles: ["ADMIN", "USER"],
      },
      {
        key: "produtos",
        label: "Produtos",
        path: "/produtos",
        icon: FilePlus,
        roles: ["ADMIN", "USER"],
      },
      {
        key: "fornecedores",
        label: "Fornecedores",
        path: "/fornecedores",
        icon: Truck,
        roles: ["ADMIN", "USER"],
      },
      {
        key: "unidades-controle",
        label: "Unidades de controle",
        path: "/unidades-controle",
        icon: Layers2,
        roles: ["ADMIN", "USER"],
      },
    ];
  }
  const cadastrosMenu = menuItems.find((item) => item.key === "cadastros");
  if (cadastrosMenu)
    cadastrosMenu.submenu = cadastrosMenu.submenu.filter(
      (item) =>
        !["produtos", "fornecedores", "unidades-controle"].includes(item.key),
    );

  const hasPermission = (roles) => {
    if (!roles || !usuario) return false;
    return roles.some((role) => possuiPermissao(role.toLowerCase()));
  };

  // Componente para MenuItem da sidebar (desktop)
  const MenuItem = ({ item, isSubmenu = false }) => {
    const hasAccess = hasPermission(item.roles);

    if (!hasAccess) return null;
    if (item.group)
      return isOpen ? (
        <p className="mx-4 mt-3 border-t border-gray-200 px-1 pb-2 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 first:mt-0 first:border-t-0 first:pt-1">
          {item.group}
        </p>
      ) : null;

    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    if (item.hasSubmenu) {
      const isExpanded = expandedMenus[item.key];
      const hasVisibleSubmenu = item.submenu?.some((subItem) =>
        hasPermission(subItem.roles),
      );

      if (!hasVisibleSubmenu) return null;

      return (
        <div key={item.key}>
          <button
            onClick={() => toggleMenu(item.key)}
            ref={(element) => {
              if (element) {
                menuButtonRefs.current[item.key] = element;
                return;
              }
              delete menuButtonRefs.current[item.key];
            }}
            className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors text-gray-700 hover:bg-gray-100`}
          >
            <div className="flex items-center gap-3">
              {Icon && <Icon size={20} />}
              {isOpen && <span className="font-medium">{item.label}</span>}
            </div>
            {isOpen &&
              (isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              ))}
          </button>

          {isExpanded && isOpen && (
            <div className="bg-white py-1">
              {item.submenu?.map((subItem) => (
                <MenuItem key={subItem.key} item={subItem} isSubmenu={true} />
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        key={item.key}
        onClick={() => navigateTo(item.path, item.key)}
        ref={(element) => {
          if (element) {
            menuButtonRefs.current[item.key] = element;
            return;
          }
          delete menuButtonRefs.current[item.key];
        }}
        className={`mx-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
          isSubmenu ? "pl-8" : ""
        } ${
          isActive
            ? "border-l-2 border-blue-600 bg-blue-50 font-medium text-blue-700 shadow-sm"
            : "border-l-2 border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        }`}
      >
        {Icon && <Icon size={18} />}
        {isOpen && (
          <span className={isSubmenu ? "text-sm" : "font-medium"}>
            {item.label}
          </span>
        )}
      </button>
    );
  };

  // Componente para Bottom Navigation (mobile)
  const BottomNavigation = () => {
    const visibleMenus = menuItems.filter((item) => hasPermission(item.roles));

    return (
      <>
        {/* Modal de opções quando um item é clicado */}
        {activeBottomMenu && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
            <div className="w-full bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    {activeBottomMenu.label}
                  </h3>
                  <button
                    onClick={() => setActiveBottomMenu(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-4">
                {activeBottomMenu.submenu?.map((subItem) => {
                  if (!hasPermission(subItem.roles)) return null;
                  if (subItem.group)
                    return (
                      <p
                        key={subItem.key}
                        className="mt-2 border-t border-gray-100 px-4 pb-2 pt-4 text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {subItem.group}
                      </p>
                    );

                  const SubIcon = subItem.icon;
                  return (
                    <button
                      key={subItem.key}
                      onClick={() => navigateTo(subItem.path)}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <SubIcon size={20} className="text-gray-600" />
                      <span className="font-medium">{subItem.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Barra inferior */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
          <div className="flex justify-around items-center py-2">
            {visibleMenus.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => {
                    if (item.hasSubmenu) {
                      setActiveBottomMenu(item);
                    } else {
                      navigateTo(item.path);
                    }
                  }}
                  className="flex flex-col items-center p-2 min-w-0"
                >
                  <Icon size={20} className="text-gray-600 mb-1" />
                  <span className="text-xs text-gray-600 truncate max-w-[60px]">
                    {item.label}
                  </span>
                </button>
              );
            })}

            {Array.isArray(sessaoOrganizacao?.organizacoes) &&
              sessaoOrganizacao.organizacoes.length > 1 && (
                <button
                  onClick={() => {
                    setShowEditProfile(false);
                    setShowOrganizationSelector(true);
                  }}
                  className="flex flex-col items-center p-2 min-w-0"
                >
                  <Building2 size={20} className="text-gray-600 mb-1" />
                  <span className="text-xs text-gray-600 truncate max-w-[60px]">
                    Org.
                  </span>
                </button>
              )}

            {/* Botão de perfil/logout */}
            <button
              onClick={() => {
                setShowOrganizationSelector(false);
                setShowEditProfile(true);
              }}
              className="flex flex-col items-center p-2 min-w-0"
            >
              <User size={20} className="text-gray-600 mb-1" />
              <span className="text-xs text-gray-600 truncate max-w-[60px]">
                Perfil
              </span>
            </button>

            <button
              onClick={handleLogout}
              className="flex flex-col items-center p-2 min-w-0"
            >
              <LogOut size={20} className="text-red-600 mb-1" />
              <span className="text-xs text-red-600 truncate max-w-[60px]">
                Sair
              </span>
            </button>
          </div>
        </div>
      </>
    );
  };

  // Se for mobile, renderizar apenas a bottom navigation
  if (isMobile) {
    return (
      <>
        <BottomNavigation />

        {showOrganizationSelector &&
          Array.isArray(sessaoOrganizacao?.organizacoes) &&
          sessaoOrganizacao.organizacoes.length > 1 && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-[55] flex items-end">
              <div className="w-full bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Organização</h3>
                    <button
                      onClick={() => setShowOrganizationSelector(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-600">
                    Selecione a organização.
                  </p>
                  <div>
                    <label
                      htmlFor="organizacao-select-mobile"
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      Organização
                    </label>
                    <select
                      id="organizacao-select-mobile"
                      value={sessaoOrganizacao?.organizacaoId || ""}
                      onChange={handleTrocaOrganizacao}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {sessaoOrganizacao.organizacoes.map((org) => (
                        <option key={org.id} value={org.id}>
                          {getNomeOrganizacaoExibicao(org)}
                          {org.cnpj ? ` - ${formatarCnpj(org.cnpj)}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Modal de Edição de Perfil */}
        {showEditProfile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Editar Perfil</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="nome"
                      value={profileData.nome}
                      onChange={handleProfileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Digite seu nome completo"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nova Senha
                      <span className="text-xs text-gray-500 ml-1">
                        (deixe em branco para manter atual)
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={profileData.password}
                        onChange={handleProfileChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nova senha (opcional)"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff size={16} className="text-gray-400" />
                        ) : (
                          <Eye size={16} className="text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Nova Senha
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={profileData.confirmPassword}
                      onChange={handleProfileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Confirme a nova senha"
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    onClick={handleSaveProfile}
                    disabled={
                      loading ||
                      !profileData.nome.trim() ||
                      !profileData.email.trim()
                    }
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Save size={16} />
                    {loading ? "Salvando..." : "Salvar"}
                  </button>

                  <button
                    onClick={() => setShowEditProfile(false)}
                    disabled={loading}
                    className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <X size={16} />
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Renderização para desktop (sidebar original)
  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => onToggle(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-full overflow-hidden bg-white shadow-lg z-50 transition-all duration-300 flex flex-col ${
          isOpen ? "w-64" : "w-16"
        } lg:relative lg:h-screen lg:translate-x-0`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {isOpen && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <CashRegisterIcon className="text-white" size={20} />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">PCOE</h2>
                <p className="text-xs text-gray-500">Gestor de Caixa</p>
              </div>
            </div>
          )}

          <button
            onClick={() => onToggle(!isOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* User Info */}
        {isOpen && usuario && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="text-blue-600" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  Bem-vindo
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {usuario.nome || "Usuário"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {isSuperAdmin
                        ? "Super Administrador"
                        : isAdmin
                          ? "Administrador"
                          : "Usuário"}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowEditProfile(true)}
                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors ml-2"
                    title="Editar Perfil"
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              </div>
            </div>
            {Array.isArray(sessaoOrganizacao?.organizacoes) &&
              sessaoOrganizacao.organizacoes.length > 1 && (
                <div className="mt-3">
                  <label
                    htmlFor="organizacao-select"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    Organização
                  </label>
                  <select
                    id="organizacao-select"
                    value={sessaoOrganizacao?.organizacaoId || ""}
                    onChange={handleTrocaOrganizacao}
                    className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {sessaoOrganizacao.organizacoes.map((org) => (
                      <option key={org.id} value={org.id}>
                        {getNomeOrganizacaoExibicao(org)}
                        {org.cnpj ? ` - ${formatarCnpj(org.cnpj)}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
          </div>
        )}

        {/* Navigation Menu */}
        <nav
          ref={navRef}
          onScroll={(event) => {
            lastNavScrollTopRef.current = event.currentTarget.scrollTop;
          }}
          className="flex-1 min-h-0 overflow-y-auto"
        >
          <div className="py-4">
            {menuItems.map((item) => (
              <MenuItem key={item.key} item={item} />
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={!isOpen ? "Sair" : ""}
          >
            <LogOut size={20} />
            {isOpen && <span className="font-medium">Sair</span>}
          </button>
        </div>
      </div>

      {/* Modal de Edição de Perfil */}
      {showEditProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh]">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Editar Perfil</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={profileData.nome}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Digite seu nome completo"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nova Senha
                    <span className="text-xs text-gray-500 ml-1">
                      (deixe em branco para manter atual)
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={profileData.password}
                      onChange={handleProfileChange}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nova senha (opcional)"
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Nova Senha
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={profileData.confirmPassword}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirme a nova senha"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleSaveProfile}
                  disabled={
                    loading ||
                    !profileData.nome.trim() ||
                    !profileData.email.trim()
                  }
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Save size={16} />
                  {loading ? "Salvando..." : "Salvar"}
                </button>

                <button
                  onClick={() => {
                    setShowEditProfile(false);
                  }}
                  disabled={loading}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <X size={16} />
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BarraNavegacao;
